import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Chip, CircularProgress, Snackbar, Alert, IconButton,
  Tooltip, Button,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const ACTION_LABELS: Record<string, string> = {
  create: 'Criação',
  update: 'Atualização',
  delete: 'Exclusão',
  status_change: 'Mudança Status',
  payment: 'Pagamento',
  cancellation: 'Cancelamento',
};

const ENTITY_LABELS: Record<string, string> = {
  transaction: 'Transação',
  receipt: 'Recibo',
  nfse: 'Nota Fiscal',
  commission: 'Comissão',
  check_received: 'Cheque Recebido',
  check_issued: 'Cheque Emitido',
  cash_register: 'Caixa',
  card_settlement: 'Baixa Cartão',
  discount: 'Desconto',
};

const ACTION_COLORS: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  create: 'success',
  delete: 'error',
  update: 'info',
  status_change: 'warning',
};

const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'transaction', label: 'Transação' },
  { value: 'receipt', label: 'Recibo' },
  { value: 'nfse', label: 'Nota Fiscal' },
  { value: 'commission', label: 'Comissão' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash_register', label: 'Caixa' },
  { value: 'card_settlement', label: 'Baixa Cartão' },
  { value: 'discount', label: 'Desconto' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'create', label: 'Criação' },
  { value: 'update', label: 'Atualização' },
  { value: 'delete', label: 'Exclusão' },
  { value: 'status_change', label: 'Mudança Status' },
  { value: 'payment', label: 'Pagamento' },
  { value: 'cancellation', label: 'Cancelamento' },
];

const FinancialAudit: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [logs, setLogs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const buildParams = useCallback(() => {
    const params: Record<string, string | number> = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (search.trim()) params.search = search.trim();
    if (entityType) params.entity_type = entityType;
    if (action) params.action = action;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [page, rowsPerPage, search, entityType, action, dateFrom, dateTo]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const res = await axios.get(getApiUrl('/financial-module/audit'), { params });
      setLogs(res.data?.data || []);
      setTotalCount(res.data?.total ?? 0);
    } catch (err: any) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erro ao carregar logs de auditoria', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClearFilters = () => {
    setSearch('');
    setEntityType('');
    setAction('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const formatDateTime = (value: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return value;
    }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight="bold">Auditoria Financeira</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchLogs} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Entidade</InputLabel>
            <Select value={entityType} label="Entidade" onChange={(e) => { setEntityType(e.target.value); setPage(0); }}>
              {ENTITY_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Ação</InputLabel>
            <Select value={action} label="Ação" onChange={(e) => { setAction(e.target.value); setPage(0); }}>
              {ACTION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="De"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <TextField
            size="small"
            type="date"
            label="Até"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <Tooltip title="Limpar filtros">
            <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
              Limpar
            </Button>
          </Tooltip>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">Nenhum registro de auditoria encontrado</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Data/Hora</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuário</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Entidade</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ação</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Campo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Valor Anterior</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Novo Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, idx) => (
                <TableRow key={log.id || idx} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(log.created_at)}
                  </TableCell>
                  <TableCell>{log.user_name || log.user_id || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={ENTITY_LABELS[log.entity_type] || log.entity_type || '-'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ACTION_LABELS[log.action] || log.action || '-'}
                      size="small"
                      color={ACTION_COLORS[log.action] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{log.field_name || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.old_value ?? '-'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.new_value ?? '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
        />
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinancialAudit;
